<div
  class="ci-select"
  [class.ci-select--invalid]="_invalid()"
  [class.ci-select--open]="open()"
  (keydown)="onKeydown($event)"
>
  <div
    #field
    cdkOverlayOrigin
    #fieldOrigin="cdkOverlayOrigin"
    class="ci-select__field"
    [class.ci-select__field--filled]="variant === 'filled'"
    [class.ci-select__field--open]="open()"
    [attr.role]="'combobox'"
    [attr.aria-expanded]="open()"
    [attr.aria-haspopup]="'listbox'"
    [attr.aria-controls]="id + '-listbox'"
    [attr.aria-label]="ariaLabel || null"
    [attr.aria-required]="required || null"
    [attr.aria-invalid]="_invalid()"
    [attr.aria-disabled]="_disabled()"
    [attr.aria-describedby]="_invalid() && errorText ? errorId : null"
    [attr.tabindex]="_disabled() ? -1 : 0"
    (mousedown)="onFieldClick()"
  >
    @if (label) {
      <label class="ci-select__field-label" [attr.for]="id + '-search'">
        {{ label }}
        @if (required) {
          <span class="ci-select__label-required" aria-hidden="true">*</span>
        }
      </label>
    }

    @if (displayValue()) {
      <span class="ci-select__value">{{ displayValue() }}</span>
    } @else {
      <span class="ci-select__placeholder">{{ placeholder }}</span>
    }

    <span class="ci-select__chevron" aria-hidden="true">
      <svg width="12" height="12" viewBox="0 0 12 12">
        <path
          d="M2 4l4 4 4-4"
          stroke="currentColor"
          stroke-width="1.5"
          fill="none"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </svg>
    </span>
  </div>

  <ng-template
    cdkConnectedOverlay
    [cdkConnectedOverlayOrigin]="fieldOrigin"
    [cdkConnectedOverlayOpen]="open()"
    [cdkConnectedOverlayPositions]="panelPositions"
    [cdkConnectedOverlayWidth]="panelWidth()"
    [cdkConnectedOverlayMinHeight]="panelMinHeight()"
    [cdkConnectedOverlayPanelClass]="shrinkPanelToFit ? 'ci-select__pane' : []"
    [cdkConnectedOverlayScrollStrategy]="scrollStrategy"
    [cdkConnectedOverlayFlexibleDimensions]="true"
    [cdkConnectedOverlayPush]="true"
    (attach)="onOverlayAttached()"
    (overlayOutsideClick)="onOutsideClick($event)"
    (detach)="onDetach()"
  >
    <div class="ci-select__panel" (keydown)="onKeydown($event)">
      <div class="ci-select__panel-header">
        <div
          class="ci-select__search-wrap"
          [class.ci-select__search-wrap--active]="!!searchTerm()"
        >
          <svg
            class="ci-select__search-icon"
            width="15"
            height="15"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path
              d="M15.5 14h-.79l-.28-.27A6.471 6.471 0 0 0 16 9.5 6.5 6.5 0 1 0 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19zM9.5 14A4.5 4.5 0 1 1 14 9.5 4.507 4.507 0 0 1 9.5 14z"
              fill="currentColor"
            />
          </svg>
          <input
            class="ci-select__search"
            type="text"
            autocomplete="off"
            [id]="id + '-search'"
            [attr.placeholder]="searchPlaceholder"
            [attr.aria-controls]="id + '-listbox'"
            [attr.aria-activedescendant]="
              focusedIndex() >= 0 ? optionId(focusedIndex()) : null
            "
            [ngModel]="searchTerm()"
            (ngModelChange)="onSearchInput($event)"
          />
          @if (searchTerm()) {
            <button
              type="button"
              class="ci-select__search-clear"
              [attr.aria-label]="searchClearLabel || null"
              (mousedown)="$event.preventDefault()"
              (click)="clearSearch($event)"
            >
              <svg width="12" height="12" viewBox="0 0 12 12" aria-hidden="true">
                <path
                  d="M1 1l10 10M11 1L1 11"
                  stroke="currentColor"
                  stroke-width="1.5"
                  stroke-linecap="round"
                />
              </svg>
            </button>
          }
        </div>
      </div>

      <ul
        class="ci-select__list"
        role="listbox"
        [attr.id]="id + '-listbox'"
      >
        @for (entry of renderItems(); track entry.item; let i = $index) {
          @let item = entry.item;
          @let selected = isSelected(item);
          @let disabled = isDisabled(item);
          <li>
            <button
              type="button"
              class="ci-select__option"
              [class.ci-select__option--selected]="selected"
              [class.ci-select__option--focused]="focusedIndex() === i"
              [class.ci-select__option--disabled]="disabled"
              [attr.id]="optionId(i)"
              [attr.role]="'option'"
              [attr.aria-selected]="selected"
              [attr.aria-disabled]="disabled"
              [disabled]="disabled"
              (mousedown)="$event.preventDefault()"
              (click)="select(item)"
            >
              <span
                class="ci-select__option-label"
                [innerHTML]="entry.html"
              ></span>
              @if (selected) {
                <svg
                  class="ci-select__check"
                  width="17"
                  height="17"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <path
                    d="M9 16.17 4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z"
                    fill="currentColor"
                  />
                </svg>
              }
            </button>
          </li>
          @if (entry.kind === 'selected') {
            <li class="ci-select__divider" aria-hidden="true"></li>
          }
        }
        @if (filtered().length === 0) {
          <li class="ci-select__empty">{{ emptyLabel }}</li>
        }
      </ul>

      @if (searchTerm()) {
        <div class="ci-select__panel-footer" aria-live="polite">
          {{ hitsText() }}
        </div>
      }
    </div>
  </ng-template>

  @if (_invalid() && errorText) {
    <p class="ci-select__error" role="alert" [id]="errorId">{{ errorText }}</p>
  }
</div>
